import { useEffect } from "react";
import { useIntl } from "react-intl";
import {
  IconRefresh,
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from "@cdkglobal/radial";
import { StyledSideSheetFooter } from "../../../../utils/styled";
import { LandingPageHeaderProps } from "../../../../types";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import {
  useRefetchRecordData,
  useRefetchRowDataList,
  useSaveRecord,
  useARRecordScheduleTypeConversion,
  useLandingPageState,
} from "../../../../hooks";
import {
  evaluateSchemaContext,
  isSetupRecordActionsRestricted,
} from "../../helpers";
import { useHeaderFeatureFlags } from "../landing-page-header/landingPageHeader.flags";
import {
  SuccessCallbackContext,
  buildOnSuccessCallback,
  HeaderHandlerContext,
  handleSave,
  handleRecordReset,
  closeSideSheet,
} from "../landing-page-header/landingPageHeader.handlers";
import { resolveHeaderVisibility } from "../landing-page-header/landingPageHeader.visibility";

// ---------------------------------------------------------------------------
// SideSheetFooter
//
// Renders the fixed action button row inside SideSheetContainer.
// Reuses the same flags / visibility / handler infrastructure as
// LandingPageHeader, but:
//   - shows only record-page buttons (no New Entry / file upload / copy dialogs)
//   - closes the side sheet via setIsSideSheetOpen instead of setIsRecordOpen
// ---------------------------------------------------------------------------

export default function SideSheetFooter({
  rowDataReducerState,
  dispatch,
}: Readonly<LandingPageHeaderProps>): JSX.Element {
  const { formatMessage } = useIntl();

  const { headerLabel, setTableTitleInfo, setupBaseRequestInfo, selected } =
    useUserContext();

  const {
    isRecordOpen,
    setIsRecordOpen,
    isSideSheetOpen,
    setIsSideSheetOpen,
    setRecordPageToast,
    setLandingPageToast,
    selectedTabSectionKey,
    setSelectedTabSectionKey,
    selectedDashboardTabSectionKey,
    setCurrentPage,
    setSearchValue,
    saveRequestId,
    activeTabIndex,
    setActiveTabIndex,
    resetSaveRequestId,
    activeAccountsReceivableRecordMigrationInfo,
    setActiveAccountsReceivableRecordMigrationInfo,
    resetConvertARScheduleTypeId,
  } = useUIStateContext();

  const {
    schema,
    rowData,
    rowDataList,
    isReadOnly,
    resetState,
    deletedObjectsList,
  } = rowDataReducerState;

  const { isLoading } = useLandingPageState(rowDataReducerState);

  const setupName = schema.title;
  const isReadOnlyRecord = isReadOnly ?? false;

  const { setupMode } = evaluateSchemaContext({
    schema,
    activeTabIndex,
    selectedDashboardTabSectionKey,
    isRecordOpen,
  });

  // ---------------------------------------------------------------------------
  // Feature flags & visibility
  // ---------------------------------------------------------------------------

  const flags = useHeaderFeatureFlags({ setupName, isReadOnly, rowData });

  const isSetupActionsLocked =
    isSetupRecordActionsRestricted(setupName, selectedDashboardTabSectionKey) &&
    flags.isPrivlibFeatureEnabled;

  const visibility = resolveHeaderVisibility({
    isLoading,
    isRecordOpen: isSideSheetOpen, // side sheet is open when isSideSheetOpen is true
    isReadOnly: isReadOnlyRecord,
    setupMode,
    columnDefs: rowDataReducerState.columnDefs,
    rowData,
    rowDataList,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    rowDataReducerState,
    flags,
    isSetupActionsLocked,
    isSetupSelected: selected.setup.value !== "",
  });

  // ---------------------------------------------------------------------------
  // Query / mutation hooks
  // ---------------------------------------------------------------------------

  const { refetch: refetchRecordData } = useRefetchRecordData({
    rowData,
    setupBaseRequestInfo,
    setIsRecordOpen,
    setLandingPageToast,
    dispatch,
  });

  const { refetch: refetchRowDataList } = useRefetchRowDataList({
    setupBaseRequestInfo,
    headerLabel,
    dispatch,
    setTableTitleInfo,
  });

  // successCallbackCtx — built before mutation hooks, omits mutation triggers
  const successCallbackCtx: SuccessCallbackContext = {
    schema,
    rowData,
    rowDataList,
    rowDataReducerState,
    resetState,
    deletedObjectsList,
    setupName,
    setupMode,
    setupBaseRequestInfo,
    selected,
    selectedTabSectionKey,
    selectedDashboardTabSectionKey,
    dispatch,
    setIsRecordOpen: () => closeSideSheet(ctx), // on success: close side sheet
    setRecordPageToast,
    setLandingPageToast,
    setActiveTabIndex,
    setSelectedTabSectionKey,
    setCurrentPage,
    setSearchValue,
    refetchRecordData: () => {
      void refetchRecordData();
    },
    refetchRowDataList: () => {
      void refetchRowDataList();
    },
    setIsSideSheetOpen,
    resetSaveRequestId,
    resetConvertARScheduleTypeId,
  };

  const { mutate: saveSingleRecord } = useSaveRecord({
    onSuccessCallback: (response) => {
      void buildOnSuccessCallback(successCallbackCtx)(response);
    },
  });

  const { mutate: convertARScheduleType } = useARRecordScheduleTypeConversion({
    onSuccessCallback: (response) => {
      void buildOnSuccessCallback(successCallbackCtx)(response);
    },
  });

  const ctx: HeaderHandlerContext = {
    ...successCallbackCtx,
    setIsRecordOpen: () => closeSideSheet(ctx), // on success: close side sheet
    saveSingleRecord,
    convertARScheduleType, // Not needed inside side sheet
    activeAccountsReceivableRecordMigrationInfo, // Not needed inside side sheet
    setActiveAccountsReceivableRecordMigrationInfo, // Not needed inside side sheet
  };

  // ---------------------------------------------------------------------------
  // Save-on-demand via saveRequestId signal
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (saveRequestId === 0) return;
    handleSave(ctx);
  }, [saveRequestId]);

  // ---------------------------------------------------------------------------
  // i18n
  // ---------------------------------------------------------------------------

  const cancelOrCloseText = isReadOnlyRecord
    ? formatMessage({ id: "button.close", defaultMessage: "Close" })
    : formatMessage({ id: "button.cancel", defaultMessage: "Cancel" });

  // ---------------------------------------------------------------------------
  // Render — record-page action buttons only
  // ---------------------------------------------------------------------------

  return (
    <StyledSideSheetFooter>
      {visibility.isClearChangesButtonVisible && (
        <TextButton
          dataTestId="ss-clear-changes-button"
          icon={<IconRefresh />}
          onClick={() => handleRecordReset(ctx)}
          size="large"
          text={formatMessage({
            id: "button.clear",
            defaultMessage: "Clear Changes",
          })}
          isDisabled={!visibility.isRecordChanged}
        />
      )}

      {visibility.shouldShowCancelOrCloseButton && (
        <SecondaryButton
          dataTestId={isReadOnlyRecord ? "ss-close-button" : "ss-cancel-button"}
          iconSize={24}
          onClick={() => closeSideSheet(ctx)}
          size="large"
          text={cancelOrCloseText}
        />
      )}

      {visibility.shouldShowSaveButton && (
        <PrimaryButton
          dataTestId="ss-save-button"
          iconColor="primary"
          iconSize={24}
          onClick={() => handleSave(ctx)}
          size="large"
          text={formatMessage({
            id: "button.save",
            defaultMessage: "Save",
          })}
          isDisabled={visibility.isSaveDisabled}
        />
      )}
    </StyledSideSheetFooter>
  );
}
